import React from 'react'
import LoadingSpinner from '../LoadingSpinner'

interface BundleOption {
  id: number
  price_delta_cents: number
  eligible_variant: {
    id: number
    name: string
    price_cents: number
    /** Statiegeld per unit — carried through so meal-deal drinks keep their deposit. */
    deposit_cents?: number
  }
}

// New grouped types (loosely typed as any downstream to keep changes minimal)
interface GroupedProductEntry {
  /** sold_out: listed but not selectable (the API refuses it at checkout). */
  product: { id: number; name: string; sold_out?: boolean }
  options: BundleOption[]
}

interface GroupedBundleGroup {
  id: number
  name: string
  required_count: number
  sort_order?: number | null
  products: GroupedProductEntry[]
}

interface GroupedBundleProductEntry {
  bundle_product: { id: number; name: string }
  groups: GroupedBundleGroup[]
}

interface MakeItAMealStepProps {
  // We now receive an array of bundle products with groups and products
  eligibleBundles: GroupedBundleProductEntry[]
  bundleVariants?: Array<{ id: number; name: string; price_cents: number }>
  selectedBundleVariantId?: number | null
  selectedBundle: {
    bundle_group_id: number
    bundle_product_id: number
    bundle_product_name: string
    price_delta_cents: number
  } | null
  selectedMealParts: Record<number, number>
  onBundleSelect: (bundle: any) => void
  onMealPartSelect: (bundleGroupId: number, variantId: number) => void
  onBundleVariantSelect?: (bundleProductId: number, variantId: number) => void
  loading: boolean
}

const MakeItAMealStep: React.FC<MakeItAMealStepProps> = ({
  eligibleBundles,
  bundleVariants,
  selectedBundleVariantId,
  selectedBundle,
  selectedMealParts,
  onBundleSelect,
  onBundleVariantSelect,
  onMealPartSelect,
  loading
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!eligibleBundles || eligibleBundles.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">🍽️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No meal deals available</h3>
        <p className="text-gray-500">This item doesn't qualify for any meal deals.</p>
      </div>
    )
  }

  // removed unused formatPrice
  const formatPriceDelta = (priceCents: number) => priceCents === 0 ? '' : `${priceCents > 0 ? '+' : ''}€${(priceCents / 100).toFixed(2)}`

  const ensureBundleSelectedForProduct = (productEntry: GroupedBundleProductEntry) => {
    const isSelected = selectedBundle?.bundle_product_id === productEntry.bundle_product.id
    if (isSelected) return
    // Compute representative price from first product with options in first group
    const firstGroup = productEntry.groups?.[0]
    const firstProductWithOption = firstGroup?.products?.find(p => (p.options || []).length > 0)
    const bundlePrice = firstProductWithOption?.options?.[0]?.price_delta_cents || 0
    onBundleSelect({
      id: productEntry.groups?.[0]?.id ?? productEntry.bundle_product.id,
      name: productEntry.bundle_product.name,
      required_count: firstGroup?.required_count ?? 0,
      bundle_product: productEntry.bundle_product,
      representative_price_delta_cents: bundlePrice
    } as any)
  }

  // removed unused getTotalMealPrice

  return (
    <div className="space-y-6">
      <div>
        {eligibleBundles.length === 1 ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Upgrade your order:</h3>
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Make it a Meal</h3>
            <p className="text-sm text-gray-500 mb-6">Upgrade your order with a meal deal</p>
          </>
        )}
      </div>

      {/* Bundle Selection with its own variant picker */}
      <div className="space-y-4">
        {eligibleBundles.map((product) => {
          const isSelected = selectedBundle?.bundle_product_id === product.bundle_product.id
          const firstGroup = product.groups?.[0]
          const firstProductWithOption = firstGroup?.products?.find(p => (p.options || []).length > 0)
          const bundlePrice = firstProductWithOption?.options?.[0]?.price_delta_cents || 0

          return (
            <div key={`bp-${product.bundle_product.id}`} className="border border-gray-200 rounded-lg p-4">
              <button
                onClick={() => ensureBundleSelectedForProduct(product)}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                  isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {product.bundle_product.name}
                    </h4>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-primary-600">{formatPriceDelta(bundlePrice)}</div>
                    {isSelected && <div className="text-xs text-primary-600 font-medium mt-1">Selected</div>}
                  </div>
                </div>
              </button>

              {/* Bundle product variants picker */}
              {isSelected && (
                <div className="mt-4 space-y-4">
                  <h5 className="font-medium text-gray-900">Choose {product.bundle_product.name} variant:</h5>
                  {/* Show actual bundle product variants fetched separately */}
                  <div className="space-y-2">
                    {(bundleVariants || []).map(v => {
                      const isSelectedVariant = Number(selectedBundleVariantId) === Number(v.id)
                      return (
                        <button
                          key={`bundle-variant-${v.id}`}
                          onClick={() => onBundleVariantSelect?.(product.bundle_product.id, Number(v.id))}
                          className={`w-full p-3 border rounded-lg text-left transition-all duration-200 ${
                            isSelectedVariant ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{v.name}</div>
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                              €{(Number(v.price_cents) / 100).toFixed(2)}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                    {(bundleVariants || []).length === 0 && (
                      <div className="text-sm text-gray-500">No variants available for this meal deal.</div>
                    )}
                  </div>

                  {/* Meal Parts Selection */}
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Choose your meal parts:</h5>
                    {product.groups.map(group => {
                      const isMain = typeof group.name === 'string' && group.name.toLowerCase().includes('main')
                      const preSelectedVariantId = selectedMealParts[group.id]
                      if (isMain && preSelectedVariantId !== undefined) return null

                      return (
                        <div key={`group-${product.bundle_product.id}-${group.id}`} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium text-gray-900">{group.name}</div>
                          </div>
                          <div className="space-y-2">
                            {(group.products || []).length === 0 && (
                              <div className="text-sm text-gray-500">No items available for this part.</div>
                            )}
                            {(group.products || []).length > 0 && (group.products || []).every((p) => p.product.sold_out) && (
                              <div className="text-sm text-gray-500">Everything in this part is sold out.</div>
                            )}
                            {(group.products || []).flatMap((p) => (
                              (p.options || []).map((option) => {
                                const isSelectedPart = selectedMealParts[group.id] === option.eligible_variant.id
                                const soldOut = !!p.product.sold_out
                                return (
                                  <button
                                    key={`opt-${group.id}-${p.product.id}-${option.id}`}
                                    disabled={soldOut}
                                    onClick={() => onMealPartSelect(group.id, option.eligible_variant.id)}
                                    className={`w-full p-3 border rounded-lg text-left transition-all duration-200 ${
                                      soldOut
                                        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                        : isSelectedPart ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium text-gray-900">{p.product.name} {option.eligible_variant.name}</div>
                                        {soldOut && <div className="text-xs font-semibold uppercase text-gray-500">Sold out</div>}
                                      </div>
                                      {option.price_delta_cents !== 0 && (
                                        <span className={`text-sm font-medium ${option.price_delta_cents > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                          {formatPriceDelta(option.price_delta_cents)}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                )
                              })
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Total Price Summary */}
      {selectedBundle && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h4 className="font-medium text-primary-900 mb-2">Meal Deal Summary</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-primary-700">Bundle surcharge:</span>
              <span className="font-medium text-primary-900">{formatPriceDelta(selectedBundle.price_delta_cents)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MakeItAMealStep
